import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Skeleton,Text } from '@driftt/dds'

import { fetchAccountAISummary } from 'api/AccountActivityAPI'
import { driftEmptyStateBuilder } from 'components/States'
import { CurrentUserContext } from 'context/currentUserContext'
import { getCurrentOrg } from 'selectors'
import { isAIEngagementSummaryEnabled } from 'utils/features'
import { segmentTrack,TrackType } from 'utils/tracking'

import useNotifications from './useNotifications'

const SHOW_FEEDBACK = 'AI_SUMMARY_SHOW_FEEDBACK'
const AI_SUMMARY = 'AI_ACCOUNT_SUMMARY'
const EMPTY_SUMMARY_TEXT = 'No new activity since last 14 days'
const ERROR_MESSAGE = 'Couldn\'t generate summary for this account.'

export const useAIEngagementSummary = (accountId: string, component: string) => {
  const headerCopy = 'AI engagement summary'
  const tooltipCopy = 'This AI engagement summary is powered by Drift AI with OpenAI'
  const feedbackCopy = 'Was this engagement summary useful?'

  const { success } = useNotifications()
  const currentUser = useContext(CurrentUserContext)
  const org = useSelector(getCurrentOrg)

  const localStorageShowFeedback = localStorage.getItem(`${accountId}_${SHOW_FEEDBACK}`)?.toLowerCase() === 'true'
  const localStorageSummary = localStorage.getItem(`${accountId}_${AI_SUMMARY}`)

  const [showFeedback, setShowFeedback] = useState<boolean>(localStorageShowFeedback)
  const [summary, setSummary] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)

  const featureDisabled = !isAIEngagementSummaryEnabled(org)

  useEffect(() => {
    if (!featureDisabled) {
      setLoading(true)
      fetchAccountAISummary(accountId)
        .then(response => setSummary(response.data[0]))
        .catch(err => {
          setSummary(ERROR_MESSAGE)
          if(component === 'Drawer'){
            segmentTrack(TrackType.DRAWER_ERROR, { error: err, source: 'AI Summary', accountId })
          } else {
            segmentTrack(TrackType.ACCOUNT_SUMMARY_ERROR, {component})
          }
        }) 
        .finally(() => setLoading(false))
    }
  }, [accountId, component, featureDisabled])

  useEffect(() => {
    if (!loading && summary !== ERROR_MESSAGE && summary !== EMPTY_SUMMARY_TEXT) {
      // create a single string for saving/comparing with local storage
      localStorage.setItem(`${accountId}_${AI_SUMMARY}`, summary)
      if (localStorageSummary !== summary && summary !== EMPTY_SUMMARY_TEXT) {
        // new ai summary, prompt for feedback again
        localStorage.setItem(`${accountId}_${SHOW_FEEDBACK}`, 'true')
        setShowFeedback(true)
      }
    }
  }, [summary, loading, accountId, localStorageSummary])

  const trackFeedback = (wasHelpful: boolean) => {
    segmentTrack(
      TrackType.ACCOUNT_SUMMARY_WAS_HELPFUL,
      {
        wasHelpful,
        email: currentUser?.email,
        roles: currentUser?.roles,
        org: currentUser?.orgId
      }
    )
    success('Thank you for your feedback.')
    setShowFeedback(false)
    // save summary to determine when to show feedback again
    localStorage.setItem(`${accountId}_${SHOW_FEEDBACK}`, 'false')
  }

  const EmptyComponent = useMemo(
    () =>
      driftEmptyStateBuilder(
        'Summary is not available',
        'No account activity in the past 7 days'
      ),
    []
  )

  let content
  if (featureDisabled) {
    content = <Text>This AI feature is currently disabled. Ask your admin to enable it for you.</Text>
  } else if (summary === EMPTY_SUMMARY_TEXT) {
    content = <EmptyComponent/>
  } else {
    content = (
      <Skeleton isLoading={loading} height="20px">
       <Text dangerouslySetInnerHTML={{ __html: summary }} />
      </Skeleton>
    )
  }
  
  return {
    showFeedback,
    trackFeedback,
    content,
    headerCopy,
    tooltipCopy,
    feedbackCopy,
  }
}